import { useCallback, useMemo, useRef, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CARRIERS,
  EXCEL_ACCEPTED_EXTENSIONS,
  EXCEL_UPLOAD_MAX_BYTES,
  resolveCarrierAlias,
  type ImportParseResult,
  type ImportPreviewRow,
  type ImportTrackInput,
  type ImportTrackResult,
} from '@ctp/shared';
import { ApiError, importApi } from '../lib/api';
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Input,
  Modal,
  Spinner,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from './ui';

/** Vorschau-Zeile + UI-Zustand (Auswahl + manuell eingetragene Reederei). */
interface EditRow extends ImportPreviewRow {
  selected: boolean;
  carrierInput: string;
}

type Step = 'upload' | 'preview' | 'carrier' | 'result';

/** Reederei-Freitext → kanonischer Code (Alias/Name/Code), sonst Großschreibung des Rohwerts. */
function manualCarrierCode(input: string): string | undefined {
  const t = input.trim();
  if (!t) return undefined;
  const a = resolveCarrierAlias(t);
  return a ? a.code : t.toUpperCase();
}

/**
 * Dashboard-Excel-Import (Lite/Terminal). Drag&Drop → Spaltenerkennung (Container/Terminal/Reederei)
 * → Vorschau → bei fehlender Reederei optionaler Dialog (eintragen ODER ablehnen = nur Terminal) →
 * Tracking. Speichert die Container über den Lite-Pfad; jede bediente Abfrage zählt gegen Trial/Limit.
 */
export function ExcelImportDialog({
  open,
  onClose,
  onImported,
  token,
}: {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
  token: string | null;
}): JSX.Element {
  const { t } = useTranslation();
  const fileInput = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('upload');
  const [parse, setParse] = useState<ImportParseResult | null>(null);
  const [rows, setRows] = useState<EditRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportTrackResult | null>(null);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const reset = useCallback((): void => {
    setStep('upload');
    setParse(null);
    setRows([]);
    setBusy(false);
    setProgress(0);
    setImporting(false);
    setResult(null);
    setError('');
    setDragOver(false);
  }, []);

  /** Schließen: nach erfolgtem Import das Dashboard aktualisieren lassen. */
  const handleClose = useCallback((): void => {
    if (result) onImported();
    reset();
    onClose();
  }, [result, onImported, onClose, reset]);

  const selectedRows = useMemo(
    () => rows.filter((r) => r.selected && r.valid && !r.duplicate),
    [rows],
  );
  const needsCarrierSelected = useMemo(
    () => selectedRows.filter((r) => r.carrierSource === 'none'),
    [selectedRows],
  );

  function updateRow(index: number, patch: Partial<EditRow>): void {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  function validateClientSide(file: File): string | null {
    const lower = file.name.toLowerCase();
    if (!EXCEL_ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext))) return t('import.errType');
    if (file.size > EXCEL_UPLOAD_MAX_BYTES)
      return t('import.errTooLarge', { max: Math.round(EXCEL_UPLOAD_MAX_BYTES / (1024 * 1024)) });
    return null;
  }

  async function handleFile(file: File): Promise<void> {
    setError('');
    const clientErr = validateClientSide(file);
    if (clientErr) {
      setError(clientErr);
      return;
    }
    setBusy(true);
    setProgress(0);
    try {
      const res = await importApi.parse(file, token, setProgress);
      setParse(res);
      setRows(
        res.rows.map((r) => ({
          ...r,
          selected: r.valid && !r.duplicate,
          carrierInput: '',
        })),
      );
      setStep('preview');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('import.errUpload'));
    } finally {
      setBusy(false);
    }
  }

  function onDrop(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  }

  async function doImport(applyManual: boolean): Promise<void> {
    const inputs: ImportTrackInput[] = selectedRows.map((r) => ({
      containerNumber: r.containerNumber,
      carrierCode: r.carrierCode ?? (applyManual ? manualCarrierCode(r.carrierInput) : undefined),
      terminal: r.terminalId,
    }));
    if (inputs.length === 0) return;
    setImporting(true);
    setError('');
    try {
      const res = await importApi.track(inputs, token);
      setResult(res);
      setStep('result');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('import.errImport'));
    } finally {
      setImporting(false);
    }
  }

  // ── Render-Bausteine ───────────────────────────────────────────────────────────────────
  function carrierCell(r: EditRow): JSX.Element {
    if (r.carrierCode) {
      return (
        <span className="inline-flex items-center gap-1.5">
          {r.carrierName ?? r.carrierCode}
          <Badge variant={r.carrierSource === 'column' ? 'primary' : 'default'}>
            {t(`import.carrierSource.${r.carrierSource}`)}
          </Badge>
        </span>
      );
    }
    return <span className="text-muted-foreground">—</span>;
  }

  function terminalCell(r: EditRow): JSX.Element {
    if (r.terminalLabel) return <Badge variant="success">{r.terminalLabel}</Badge>;
    if (r.terminalRaw) {
      return (
        <span className="text-xs text-muted-foreground" title={r.terminalRaw}>
          {r.terminalRaw.length > 28 ? `${r.terminalRaw.slice(0, 28)}…` : r.terminalRaw}{' '}
          <span className="italic">({t('import.terminalUnmatched')})</span>
        </span>
      );
    }
    return <span className="text-muted-foreground">—</span>;
  }

  function statusCell(r: EditRow): JSX.Element {
    if (!r.valid) return <Badge variant="danger">{t('import.status.invalid')}</Badge>;
    if (r.duplicate) return <Badge variant="warning">{t('import.status.duplicate')}</Badge>;
    if (r.carrierCode) return <Badge variant="success">{t('import.status.trackable')}</Badge>;
    if (r.terminalId) return <Badge variant="primary">{t('import.status.terminalOnly')}</Badge>;
    return <Badge variant="warning">{t('import.status.needsCarrier')}</Badge>;
  }

  const title =
    step === 'carrier'
      ? t('import.carrierTitle')
      : step === 'result'
        ? t('import.resultTitle')
        : t('import.title');

  // ── Footer je Schritt ──────────────────────────────────────────────────────────────────
  let footer: JSX.Element | null = null;
  if (step === 'preview' && parse && rows.length > 0) {
    footer = (
      <>
        <span className="mr-auto text-sm text-muted-foreground">
          {t('import.selectedCount', { count: selectedRows.length })}
        </span>
        <Button variant="secondary" onClick={handleClose} disabled={importing}>
          {t('import.cancel')}
        </Button>
        {needsCarrierSelected.length > 0 && (
          <Button variant="secondary" onClick={() => setStep('carrier')} disabled={importing}>
            {t('import.fillCarrier')}
          </Button>
        )}
        <Button onClick={() => void doImport(false)} disabled={importing || selectedRows.length === 0}>
          {importing ? <Spinner /> : t('import.importNow')}
        </Button>
      </>
    );
  } else if (step === 'carrier') {
    footer = (
      <>
        <Button variant="secondary" onClick={() => setStep('preview')} disabled={importing}>
          {t('import.back')}
        </Button>
        <Button variant="secondary" onClick={() => void doImport(false)} disabled={importing}>
          {t('import.decline')}
        </Button>
        <Button onClick={() => void doImport(true)} disabled={importing}>
          {importing ? <Spinner /> : t('import.applyImport')}
        </Button>
      </>
    );
  } else if (step === 'result') {
    footer = <Button onClick={handleClose}>{t('import.done')}</Button>;
  } else {
    footer = (
      <Button variant="secondary" onClick={handleClose}>
        {t('import.cancel')}
      </Button>
    );
  }

  return (
    <Modal open={open} onClose={handleClose} title={title} footer={footer} size="xl">
      {error && (
        <Alert kind="error" className="mb-4">
          {error}
        </Alert>
      )}

      {/* ── Upload ─────────────────────────────────────────────────────── */}
      {step === 'upload' && (
        <div
          role="button"
          tabIndex={0}
          onClick={() => fileInput.current?.click()}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && fileInput.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-12 text-center transition-colors ${
            dragOver ? 'border-primary bg-primary/5' : 'border-input hover:border-primary/50'
          }`}
        >
          {busy ? (
            <>
              <Spinner className="h-6 w-6" />
              <p className="text-sm text-muted-foreground">{t('import.analyzing')}</p>
              <div className="mt-2 h-2 w-56 overflow-hidden rounded-full bg-muted">
                <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
              </div>
            </>
          ) : (
            <>
              <p className="font-medium">{t('import.dropzone')}</p>
              <p className="max-w-md text-sm text-muted-foreground">{t('import.dropzoneHint')}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {EXCEL_ACCEPTED_EXTENSIONS.join(', ')} ·{' '}
                {t('import.maxSize', { max: Math.round(EXCEL_UPLOAD_MAX_BYTES / (1024 * 1024)) })}
              </p>
            </>
          )}
          <input
            ref={fileInput}
            type="file"
            accept={EXCEL_ACCEPTED_EXTENSIONS.join(',')}
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleFile(file);
              e.target.value = '';
            }}
          />
        </div>
      )}

      {/* ── Vorschau ───────────────────────────────────────────────────── */}
      {step === 'preview' && parse && (
        <div className="space-y-4">
          <div className="rounded-md border bg-muted/30 p-3 text-sm">
            <p className="font-medium">
              {t('import.detectedTitle')}
              {parse.detection.sheetName && (
                <span className="ml-2 font-normal text-muted-foreground">
                  {t('import.sheet', { sheet: parse.detection.sheetName })}
                </span>
              )}
            </p>
            <div className="mt-1.5 flex flex-wrap gap-2">
              <Badge variant="default">
                {t('import.detectedContainer', { header: parse.detection.containerHeader ?? '—' })}
              </Badge>
              {parse.detection.terminalColumn != null ? (
                <Badge variant="success">
                  {t('import.detectedTerminal', { header: parse.detection.terminalHeader ?? '—' })}
                </Badge>
              ) : (
                <Badge variant="warning">{t('import.noTerminalColumn')}</Badge>
              )}
              {parse.detection.carrierColumn != null ? (
                <Badge variant="primary">
                  {t('import.detectedCarrier', { header: parse.detection.carrierHeader ?? '—' })}
                </Badge>
              ) : (
                <Badge variant="default">{t('import.noCarrierColumn')}</Badge>
              )}
            </div>
            {rows.length > 0 && (
              <p className="mt-2 text-muted-foreground">
                {t('import.summary', {
                  valid: parse.summary.valid,
                  withTerminal: parse.summary.withTerminal,
                  withCarrier: parse.summary.withCarrier,
                  needsCarrier: parse.summary.needsCarrier,
                })}
              </p>
            )}
          </div>

          {rows.length === 0 ? (
            <EmptyState title={t('import.emptyTitle')} hint={t('import.emptyHint')} />
          ) : (
            <>
              {needsCarrierSelected.length > 0 && (
                <Alert kind="warn">
                  {t('import.needsCarrierNotice', { count: needsCarrierSelected.length })}
                </Alert>
              )}
              <div className="max-h-[45vh] overflow-y-auto rounded-md border">
                <Table>
                  <THead>
                    <TR>
                      <TH className="w-10" />
                      <TH>{t('import.col.number')}</TH>
                      <TH>{t('import.col.carrier')}</TH>
                      <TH>{t('import.col.terminal')}</TH>
                      <TH>{t('import.col.status')}</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {rows.map((r, i) => (
                      <TR key={`${r.containerNumber}-${r.rowIndex}`} className={r.selected ? '' : 'opacity-50'}>
                        <TD>
                          <input
                            type="checkbox"
                            checked={r.selected}
                            disabled={!r.valid || r.duplicate}
                            aria-label={t('import.col.select')}
                            onChange={(e) => updateRow(i, { selected: e.target.checked })}
                          />
                        </TD>
                        <TD className="font-mono">{r.containerNumber}</TD>
                        <TD>{carrierCell(r)}</TD>
                        <TD>{terminalCell(r)}</TD>
                        <TD>{statusCell(r)}</TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Reederei-Dialog ────────────────────────────────────────────── */}
      {step === 'carrier' && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{t('import.carrierHint')}</p>
          <datalist id="ctp-carrier-options">
            {CARRIERS.map((c) => (
              <option key={c.code} value={c.code}>
                {c.name}
              </option>
            ))}
          </datalist>
          <div className="max-h-[50vh] space-y-2 overflow-y-auto">
            {rows.map((r, i) =>
              r.selected && r.valid && !r.duplicate && r.carrierSource === 'none' ? (
                <div
                  key={`${r.containerNumber}-${r.rowIndex}`}
                  className="flex flex-wrap items-center gap-3 rounded-md border p-2.5"
                >
                  <span className="font-mono text-sm">{r.containerNumber}</span>
                  {r.terminalLabel ? (
                    <Badge variant="success">{r.terminalLabel}</Badge>
                  ) : (
                    <span className="text-xs italic text-muted-foreground">
                      {t('import.noTerminalForRow')}
                    </span>
                  )}
                  <Input
                    className="ml-auto w-44"
                    list="ctp-carrier-options"
                    value={r.carrierInput}
                    placeholder={t('import.carrierPlaceholder')}
                    onChange={(e) => updateRow(i, { carrierInput: e.target.value })}
                  />
                </div>
              ) : null,
            )}
          </div>
        </div>
      )}

      {/* ── Ergebnis ───────────────────────────────────────────────────── */}
      {step === 'result' && result && (
        <div className="space-y-4">
          {importing ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Spinner /> {t('import.importing')}
            </div>
          ) : (
            <>
              <p className="text-sm">
                {t('import.resultSummary', {
                  tracked: result.summary.tracked,
                  terminalOnly: result.summary.terminalOnly,
                  skipped: result.summary.skipped,
                })}
              </p>
              {result.summary.limitReached && <Alert kind="warn">{t('import.limitReached')}</Alert>}
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
